import { faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { motion, useAnimation } from 'framer-motion'
import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react'
import * as SiteWideStyles from '../../../../styles/abstract/SiteWide.css'
import * as HeaderMobileMenuItemStyles from '../../../../styles/foundation/HeaderMobileMenuItem.css'

type HeaderMobileMenuItemProps = {
  children: React.ReactNode
  category: string
  currentMenuCategory: HTMLLIElement | null
  setCurrentMenuCategory: Dispatch<SetStateAction<HTMLLIElement | null>>
}

export const HeaderMobileMenuItem = ({
  children,
  category,
  currentMenuCategory,
  setCurrentMenuCategory,
}: HeaderMobileMenuItemProps) => {
  const [isOpen, setOpenState] = useState(false)
  const [closedHeight, setClosedHeight] = useState(0)
  const containerElement = useRef<HTMLLIElement>(null!)
  const categoryElement = useRef<HTMLButtonElement>(null!)

  const controls = useAnimation()

  const variants = {
    close: {
      height: `${closedHeight}px`,
      transition: { duration: 0.35, ease: [0.86, 0.0, 0.07, 1.0] },
    },
    open: {
      height: 'auto',
      transition: { delay: 0.15, duration: 0.4, ease: [1.0, 0.0, 0.0, 1.0] },
    },
  }

  const onClick = (event: React.MouseEvent<HTMLLIElement>) => {
    if (!isOpen) setCurrentMenuCategory(event.currentTarget)
    else setCurrentMenuCategory(null)
  }

  useEffect(() => {
    setOpenState(currentMenuCategory === containerElement.current)
  }, [currentMenuCategory])

  useEffect(() => {
    if (isOpen) controls.start('open')
    else controls.start('close')
  }, [isOpen])

  useEffect(() => {
    setClosedHeight(categoryElement?.current.clientHeight + 1)

    window.addEventListener('resize', () => {
      if (!categoryElement?.current) return
      setClosedHeight(categoryElement?.current.clientHeight + 1)
    })
  }, [])

  useEffect(() => {
    controls.start('close')
  }, [closedHeight])

  return (
    <motion.li
      animate={controls}
      variants={variants}
      ref={containerElement}
      onClick={onClick}
      className={classNames(HeaderMobileMenuItemStyles.Container, isOpen && 'Container-open')}
    >
      <button
        type="button"
        ref={categoryElement}
        className={classNames(HeaderMobileMenuItemStyles.Category, SiteWideStyles.Inner)}
      >
        {category}
        <FontAwesomeIcon icon={faChevronDown} width="12" />
      </button>
      <div className={HeaderMobileMenuItemStyles.Content}>
        <div className={SiteWideStyles.Inner}>{children}</div>
      </div>
    </motion.li>
  )
}
